// i18n
import { useTranslation } from 'react-i18next';

// mui
import { Box, Grid, Paper, Typography } from '@mui/material';

// types
import { Contract, ContractV2, User } from '../../../../@types';

// ui
import { ChipStatus } from '../../../ui/ChipStatus/ChipStatus';
import { SectionHeader } from '../../../ui/SectionHeader/SectionHeader';
import { UserAvatarModified } from '../../../ui/UserAvatarModified/UserAvatarModified';

// utils
import { formatCurrency } from '../../../../utils/currency';
import { formatDate } from '../../../../utils/dates';
import { translateEntityType } from '../../../../utils/translations';

// props
export interface DetailsProps {
	contract: Contract | ContractV2;
}

export const Details = ({ contract }: DetailsProps) => {
	// hooks
	const { t } = useTranslation();

	// V1 and V2 contracts now share `num` / `description` / `dateSigning`, so
	// read those directly off the union. The remaining fields are V2-only and
	// still need the discriminator — `totalPaid` is present only on the V2 shape.
	const isV2 = 'totalPaid' in contract;

	const contractNum = contract.num || `#${contract.id}`;
	const description = contract.description;
	const dateSigned = contract.dateSigning ? String(contract.dateSigning) : null;
	const lastUpdate = isV2 ? (contract as ContractV2).lastUpdate : undefined;
	const commitmentAmount = isV2 ? (contract as ContractV2).commitmentAmount : null;
	const activityName = isV2 ? (contract as ContractV2).activityName : null;
	const workPlanName = isV2 ? (contract as ContractV2).workPlanName : null;

	// Both Entity and ContractEntityV2 share { id, name, type } for display purposes.
	// V1 entities can also be bare IDs — filter those out.
	type EntityItem = { id: number; name: string; type: string };
	const entityList = ((contract.entities || []) as (EntityItem | number)[]).filter(
		(e): e is EntityItem => typeof e !== 'number'
	);
	const entitySupplier = entityList.find((e) => e.type === 'SUPPLIER');
	const entitiesOther = entityList.filter((e) => e.type !== 'SUPPLIER');

	const amounts = contract.amounts || [];

	return (
		<Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
			<SectionHeader
				IconCircleProps={{ type: 'contract' }}
				title={t('details')}
				actions={contract.status && <ChipStatus status={contract.status} />}
			/>
			<Box sx={{ p: { xs: 2, sm: 3 } }}>
				<Grid container spacing={1}>
					<Grid item xs={12} sm={6}>
						<Typography color="text.secondary" variant="caption">
							{t('supplier')}
						</Typography>
						<Typography fontWeight={600}>{entitySupplier?.name || '—'}</Typography>
					</Grid>
					<Grid item xs={12} sm={6}>
						<Typography color="text.secondary" variant="caption">
							{t('number')}
						</Typography>
						<Typography fontWeight={600}>{contractNum}</Typography>
					</Grid>
					{lastUpdate?.at && (
						<Grid item xs={12} sm={6}>
							<Typography color="text.secondary" variant="caption">
								{t('lastUpdated')}
							</Typography>
							<UserAvatarModified
								date={lastUpdate.at}
								user={
									lastUpdate.actor?.name
										? ({ firstName: lastUpdate.actor.name } as Partial<User>)
										: null
								}
								variant="compact"
							/>
						</Grid>
					)}
					{description && (
						<Grid item xs={12}>
							<Typography color="text.secondary" variant="caption">
								{t('description')}
							</Typography>
							<Typography variant="body2">{description}</Typography>
						</Grid>
					)}
					<Grid item xs={12} sm={6}>
						<Typography color="text.secondary" variant="caption">
							{t('amount')}
						</Typography>
						{amounts.length === 0 && <Typography variant="body2">—</Typography>}
						{amounts.map((a) => (
							<Typography key={a.currency} variant="body2">
								{formatCurrency({ currency: a.currency, value: a.amount })}
							</Typography>
						))}
					</Grid>
					{dateSigned && (
						<Grid item xs={12} sm={6}>
							<Typography color="text.secondary" variant="caption">
								{t('dateSigning')}
							</Typography>
							<Typography variant="body2">{formatDate({ date: new Date(dateSigned) })}</Typography>
						</Grid>
					)}
					{commitmentAmount != null && (
						<Grid item xs={12} sm={6}>
							<Typography color="text.secondary" variant="caption">
								{t('commitmentAmount')}
							</Typography>{' '}
							<Typography variant="body2">
								{formatCurrency({
									currency: amounts[0]?.currency || 'USD',
									value: commitmentAmount,
								})}
							</Typography>
						</Grid>
					)}
					{activityName && (
						<Grid item xs={12} sm={6}>
							<Typography color="text.secondary" variant="caption">
								{t('activity')}
							</Typography>
							<Typography variant="body2">{activityName}</Typography>
						</Grid>
					)}
					{workPlanName && (
						<Grid item xs={12} sm={6}>
							<Typography color="text.secondary" variant="caption">
								{t('workPlan')}
							</Typography>
							<Typography variant="body2">{workPlanName}</Typography>
						</Grid>
					)}
					{entitiesOther.map((e) => (
						<Grid item xs={12} sm={6} key={`${e.type}-${e.id}`}>
							<Typography color="text.secondary" variant="caption">
								{translateEntityType(e.type, t)}
							</Typography>
							<Typography variant="body2">{e.name}</Typography>
						</Grid>
					))}
				</Grid>
			</Box>
		</Paper>
	);
};
